import cn from "classnames";
import { Link, generatePath } from "react-router-dom";
import Styles from "./PitchSmartCards.module.scss";
import { ROUTES } from "src/utils/constants/routes";
import { PitchSmartBracket, PitchSmartTableData, dayCellColor, statusColor } from "src/utils/pitch-smart";
import {
  fourDigitNumberFormatter,
  inningsFormatter,
  percentFormatter,
} from "src/components/pages/Teams/stats-columns/formatters";

interface PitchSmartCardsProps {
  rows: PitchSmartTableData[];
  bracket: PitchSmartBracket;
  teamID: string;
}

const DAY_LABELS = ["Today", "-1", "-2", "-3", "-4", "-5"];

/** Split a "First Last, 12" name string into its name and jersey-number parts. */
function splitName(full: string): { name: string; number: string } {
  const idx = full.lastIndexOf(",");
  if (idx === -1) return { name: full, number: "" };
  return { name: full.slice(0, idx).trim(), number: full.slice(idx + 1).trim() };
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={Styles.stat}>
      <span className={Styles.statValue}>{value}</span>
      <span className={Styles.statLabel}>{label}</span>
    </div>
  );
}

/**
 * Touch-friendly card list for the Pitch Smart view, shown instead of the wide
 * AG-Grid on small screens. Each card leads with the pitcher's availability —
 * the one thing a coach needs at a glance — then recent pitch counts and season
 * stats. Colors come from the same helpers as the grid, so the two never drift.
 */
export default function PitchSmartCards({ rows, bracket, teamID }: PitchSmartCardsProps) {
  return (
    <div className={Styles.cards}>
      {rows.map((row) => {
        const { name, number } = splitName(row.name);
        const counts = [row.TODAY, row.DAY1, row.DAY2, row.DAY3, row.DAY4, row.DAY5];
        return (
          <article key={row.name} className={Styles.card}>
            <header className={Styles.cardHeader}>
              {row.id && teamID ? (
                <Link
                  className={Styles.playerName}
                  to={generatePath(ROUTES.teams.playerProfile, { pid: row.id, id: teamID })}
                >
                  {name}
                </Link>
              ) : (
                <span className={Styles.playerName}>{name}</span>
              )}
              {number && <span className={Styles.playerNumber}>#{number}</span>}
            </header>

            <div className={Styles.status} style={{ backgroundColor: statusColor(row.availability) }}>
              {row.availability.label}
            </div>

            <div className={Styles.section}>
              <div className={Styles.sectionLabel}>Recent pitch counts</div>
              <div className={Styles.days}>
                {counts.map((count, i) => {
                  const bg = dayCellColor(count, bracket);
                  return (
                    <div key={DAY_LABELS[i]} className={Styles.day}>
                      <span className={Styles.dayLabel}>{DAY_LABELS[i]}</span>
                      <span
                        className={cn(Styles.dayCount, { [Styles.dayEmpty]: !count })}
                        style={bg ? { backgroundColor: bg } : undefined}
                      >
                        {count || "–"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={Styles.stats}>
              <Stat label="IP" value={inningsFormatter({ value: row.IP })} />
              <Stat label="#P" value={String(row["#P"] ?? 0)} />
              <Stat label="S%" value={percentFormatter({ value: row["S%"] })} />
              <Stat label="SO%" value={percentFormatter({ value: row["SO%"] })} />
              <Stat label="WHIP" value={fourDigitNumberFormatter({ value: row.WHIP })} />
              <Stat label="BAA" value={fourDigitNumberFormatter({ value: row.BAA })} />
            </div>
          </article>
        );
      })}
    </div>
  );
}
